import React, {useState} from "react";
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Col,
    message,
    Modal,
    Row,
    Space,
    Statistic,
    Table,
    Tag,
    Typography
} from "antd";
import "./BackofficeMatchableInvoicesPage.css";

import {useStore, useFetch} from "@vigo/hooks";
import moment from "moment";
import {useTranslation} from "react-i18next";
import {
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined
} from "@ant-design/icons";
import {formatMoney2} from "../../../utils/moneyHelper";
import {codPaymentReferenceCodeStatuses} from "../../../constants/constants";
import {ResponsiveDataTable} from "components/_defaultComponents/ResponsiveDataTable";

const {Title, Text} = Typography;

export const BackofficeMatchableInvoicesPage = ({isTabOpened = true}) => {
    const stores = useStore();
    const {t} = useTranslation();
    const [selectedMatches, setSelectedMatches] = useState([]);
    const [isMatchModalVisible, setIsMatchModalVisible] = useState(false);

    const [matchingLoading, setMatchingLoading] = useState(false);
    const [recall, setRecall] = useState(false);

    const [loading, matchableInvoicesResult] = useFetch({
        fetchFn: stores.codPaymentStore.getMatchablePendingInvoices.bind(null),
        dependencies: [isTabOpened, recall],
        initialCall: isTabOpened,
        condition: isTabOpened,
        waitForResponse: true
    });

    const handleMatchSelection = (match, checked) => {
        if (checked) {
            setSelectedMatches(prev => [...prev, match]);
        } else {
            setSelectedMatches(prev =>
                prev.filter(
                    m =>
                        m.pendingInvoice._id !== match.pendingInvoice._id ||
                        m.netsisInvoice.document_id !== match.netsisInvoice.document_id
                )
            );
        }
    };

    const handleSelectAll = checked => {
        if (checked) {
            setSelectedMatches(matchableInvoicesResult?.data?.matchableInvoices || []);
        } else {
            setSelectedMatches([]);
        }
    };

    const handleMatchInvoices = async () => {
        if (selectedMatches.length === 0) {
            message.warning("Lütfen eşleştirilecek faturaları seçiniz.");
            return;
        }

        setMatchingLoading(true);
        try {
            const result = await stores.codPaymentStore.matchPendingInvoices({
                matchedInvoices: selectedMatches
            });

            if (result.success) {
                message.success(`${selectedMatches.length} fatura başarıyla eşleştirildi.`);
                setSelectedMatches([]);
                setIsMatchModalVisible(false);
                setRecall(prev => !prev); // Refresh data
            } else {
                message.error(result.message || "Eşleştirme sırasında bir hata oluştu.");
            }
        } catch (error) {
            message.error("Eşleştirme sırasında bir hata oluştu.");
        } finally {
            setMatchingLoading(false);
        }
    };

    const renderMatchTypeTag = matchType => {
        let color = "default";
        let icon = null;

        if (matchType.includes("receivedAmount")) {
            color = "green";
            icon = <CheckCircleOutlined />;
        } else if (matchType.includes("payableAmount")) {
            color = "blue";
            icon = <InfoCircleOutlined />;
        } else if (matchType.includes("providerPayableAmount")) {
            color = "orange";
            icon = <InfoCircleOutlined />;
        } else if (matchType.includes("DocumentId") || matchType.includes("Referans kod")) {
            color = "purple";
            icon = <CheckCircleOutlined />;
        }

        return (
            <Tag color={color} icon={icon}>
                {matchType}
            </Tag>
        );
    };

    const columns = [
        {
            title: "Seç",
            key: "select",
            width: 60,
            render: (_, record) => (
                <Checkbox
                    checked={selectedMatches.some(
                        match =>
                            match.pendingInvoice._id === record.pendingInvoice._id &&
                            match.netsisInvoice.document_id === record.netsisInvoice.document_id
                    )}
                    onChange={e => handleMatchSelection(record, e.target.checked)}
                />
            ),
            onCell: () => ({
                "data-label": "Seç"
            })
        },
        {
            title: "Referans Kodu",
            dataIndex: ["pendingInvoice", "referenceCode"],
            key: "referenceCode",
            render: (text, record) => record.pendingInvoice?.referenceCode || "-",
            onCell: () => ({
                "data-label": "Referans Kodu"
            })
        },
        {
            title: "Cari Kodu",
            dataIndex: ["pendingInvoice", "cariCode"],
            key: "cariCode",
            render: (text, record) => record.pendingInvoice?.cariCode || "-",
            onCell: () => ({
                "data-label": "Cari Kodu"
            })
        },
        {
            title: "Fatura No",
            dataIndex: ["pendingInvoice", "invoice", "documentId"],
            key: "pendingInvoiceDocumentId",
            render: (text, record) => record.pendingInvoice?.invoice?.documentId || "-",
            onCell: () => ({
                "data-label": "Fatura No"
            })
        },
        {
            title: "Fatura No (Netsis)",
            dataIndex: ["netsisInvoice", "document_id"],
            key: "netsisInvoiceDocumentId",
            render: (text, record) => record.netsisInvoice?.document_id || "-",
            onCell: () => ({
                "data-label": "Fatura No (Netsis)"
            })
        },
        {
            title: "Tutar",
            dataIndex: ["pendingInvoice", "receivedAmount"],
            key: "receivedAmount",
            render: (text, record) =>
                record.pendingInvoice?.receivedAmount?.toLocaleString("tr-TR", {
                    style: "currency",
                    currency: "TRY"
                }) || "-",
            onCell: () => ({
                "data-label": "Tutar"
            })
        },
        {
            title: "Tutar (Netsis)",
            dataIndex: ["netsisInvoice", "total_amount"],
            key: "netsisTotalAmount",
            render: (text, record) =>
                record.netsisInvoice?.total_amount?.toLocaleString("tr-TR", {
                    style: "currency",
                    currency: "TRY"
                }) || "-",
            onCell: () => ({
                "data-label": "Tutar (Netsis)"
            })
        },
        {
            title: "Eşleşme Tipi",
            dataIndex: "matchType",
            key: "matchType",
            render: (text, record) => {
                const matchType = record.matchType;
                if (!matchType) return "-";

                return (
                    <Tag
                        color={renderMatchTypeTag(matchType).color}
                        icon={renderMatchTypeTag(matchType).icon}
                    >
                        {matchType}
                    </Tag>
                );
            },
            onCell: () => ({
                "data-label": "Eşleşme Tipi"
            })
        },
        {
            title: "Durum",
            dataIndex: ["pendingInvoice", "status"],
            key: "status",
            render: (text, record) => {
                const status = record.pendingInvoice?.status;
                const statusObj = codPaymentReferenceCodeStatuses.find(s => s.value === status);
                if (statusObj) return statusObj.name;
                if (status) return status;
                return "-";
            },
            onCell: () => ({
                "data-label": "Durum"
            })
        },
        {
            title: "Vade Tarihi",
            dataIndex: ["pendingInvoice", "dueDate"],
            key: "dueDate",
            render: (text, record) =>
                record.pendingInvoice?.dueDate
                    ? moment(record.pendingInvoice.dueDate).format("YYYY-MM-DD")
                    : "-",
            onCell: () => ({
                "data-label": "Vade Tarihi"
            })
        }
    ];

    const data = matchableInvoicesResult?.data || {};
    const matchableInvoices = data.matchableInvoices || [];

    // Debug için console.log ekleyelim

    return (
        <Card className="cod-matchable-invoices-page">
            <Row gutter={[16, 16]} style={{marginBottom: 24}}>
                <Col xs={12} sm={12} md={6} lg={6}>
                    <Statistic
                        title="Bekleyen Fatura"
                        value={data.pendingInvoicesCount || 0}
                        prefix={<ExclamationCircleOutlined style={{color: "#faad14"}} />}
                    />
                </Col>
                <Col xs={12} sm={12} md={6} lg={6}>
                    <Statistic
                        title="Netsis Fatura"
                        value={data.netsisInvoicesCount || 0}
                        prefix={<InfoCircleOutlined style={{color: "#1890ff"}} />}
                    />
                </Col>
                <Col xs={12} sm={12} md={6} lg={6}>
                    <Statistic
                        title="Eşleştirilebilir"
                        value={data.matchableInvoicesCount || 0}
                        prefix={<CheckCircleOutlined style={{color: "#52c41a"}} />}
                    />
                </Col>
                <Col xs={12} sm={12} md={6} lg={6}>
                    <Statistic
                        title="Seçili"
                        value={selectedMatches.length}
                        prefix={<CheckCircleOutlined style={{color: "#722ed1"}} />}
                    />
                </Col>
            </Row>

            <Row
                gutter={[12, 12]}
                style={{marginBottom: 16}}
                justify="space-between"
                align="middle"
            >
                <Col xs={24} md="auto">
                    <Title level={4}>Eşleştirilebilir Faturalar</Title>
                </Col>
                <Col xs={24} md="auto">
                    <Space wrap className="cod-matchable-invoices-actions">
                        <Button
                            className="cod-matchable-invoices-action-btn"
                            onClick={() => handleSelectAll(true)}
                            disabled={matchableInvoices.length === 0}
                        >
                            Tümünü Seç
                        </Button>
                        <Button
                            className="cod-matchable-invoices-action-btn"
                            onClick={() => handleSelectAll(false)}
                            disabled={selectedMatches.length === 0}
                        >
                            Seçimi Kaldır
                        </Button>
                        <Button
                            className="cod-matchable-invoices-action-btn"
                            type="primary"
                            onClick={() => setIsMatchModalVisible(true)}
                            disabled={selectedMatches.length === 0}
                        >
                            Seçili Faturaları Eşleştir ({selectedMatches.length})
                        </Button>
                    </Space>
                </Col>
            </Row>

            <ResponsiveDataTable
                loading={loading}
                rowKey={record =>
                    record.pendingInvoice?._id || record.netsisInvoice?.document_id || Math.random()
                }
                columns={columns}
                dataSource={matchableInvoices}
                pagination={{pageSize: 20}}
                size="small"
            />

            <Modal
                title={`Fatura Eşleştirme Onayı (${selectedMatches.length} adet)`}
                visible={isMatchModalVisible}
                onOk={handleMatchInvoices}
                onCancel={() => setIsMatchModalVisible(false)}
                confirmLoading={matchingLoading}
                okText="Eşleştir"
                cancelText="İptal"
                width={600}
            >
                <Alert
                    message={`${selectedMatches.length} adet fatura eşleştirilecek.`}
                    description="Bu işlem geri alınamaz. Devam etmek istediğinizden emin misiniz?"
                    type="warning"
                    showIcon
                    style={{marginBottom: 16}}
                />

                <ResponsiveDataTable
                    columns={[
                        {
                            title: "Referans Kodu",
                            dataIndex: ["pendingInvoice", "referenceCode"],
                            key: "referenceCode",
                            width: 150,
                            onCell: () => ({
                                "data-label": "Referans Kodu"
                            })
                        },
                        {
                            title: "Bekleyen Tutar",
                            dataIndex: ["pendingInvoice", "receivedAmount"],
                            key: "pendingAmount",
                            width: 120,
                            render: text => formatMoney2({amount: text}),
                            onCell: () => ({
                                "data-label": "Bekleyen Tutar"
                            })
                        },
                        {
                            title: "Netsis Fatura No",
                            dataIndex: ["netsisInvoice", "document_id"],
                            key: "netsisDocumentId",
                            width: 150,
                            onCell: () => ({
                                "data-label": "Netsis Fatura No"
                            })
                        },
                        {
                            title: "Netsis Tutar",
                            dataIndex: ["netsisInvoice", "total_amount"],
                            key: "netsisAmount",
                            width: 120,
                            onCell: () => ({
                                "data-label": "Netsis Tutar"
                            })
                        }
                    ]}
                    dataSource={selectedMatches}
                    rowKey={record =>
                        `${record.pendingInvoice._id}-${record.netsisInvoice.document_id}`
                    }
                    pagination={false}
                    size="small"
                />
            </Modal>
        </Card>
    );
};
